import { useQuery } from "@apollo/client";
import { useEffect, useMemo } from "react";
import {
  GetProfileNotesWebsocketTokenQueryQuery,
  GetProfileNotesWebsocketTokenQueryQueryVariables,
} from "types/graphql-schema";

import { currentUserVar } from "@cache/cache";
import Loading from "@components/loading/loading";
import {
  destroyYDocAndWebsocketProvider,
  getYDocAndWebsocketProvider,
} from "@components/wysiwyg/helpers";
import WYSIWYG from "@components/wysiwyg/wysiwyg";

import getProfileNotesWebsocketTokenQuery from "./graphql/get-profile-notes-websocket-token-query";

const ProfilePrivateNotes = ({ profileId }: { profileId: number }) => {
  const currentUser = currentUserVar();

  const { data, loading } = useQuery<
    GetProfileNotesWebsocketTokenQueryQuery,
    GetProfileNotesWebsocketTokenQueryQueryVariables
  >(getProfileNotesWebsocketTokenQuery, {
    variables: { profileId },
    fetchPolicy: "network-only",
  });

  const websocketToken = data?.user?.profilePrivateNotesWebsocketToken || "";
  const socketId = `privatenotes${currentUser.id}-${profileId}`;

  const websocketContext = useMemo(() => {
    if (!websocketToken) return null;
    return getYDocAndWebsocketProvider(socketId, websocketToken);
  }, [socketId, websocketToken]);

  useEffect(() => {
    return () => {
      if (websocketContext) {
        destroyYDocAndWebsocketProvider(socketId);
      }
    };
  }, []);

  if (!waffle.flag_is_active("private-notes")) {
    return null;
  }

  if (loading) {
    return <Loading>Loading private notes...</Loading>;
  }

  if (!websocketContext) {
    return <div>Unable to load notes</div>;
  }

  const { ydoc, providerWebsocket } = websocketContext;

  return (
    <div className="flex flex-col">
      <div className="mx-auto max-w-[800px] w-full">
        <div className="font-medium text-gray-500 mt-4 mb-5">Private notes</div>
        <WYSIWYG
          ydoc={ydoc}
          providerWebsocket={providerWebsocket}
          webSocketDocumentId={socketId}
          uploadVariable={{ artifactId: undefined }}
          value={null}
          editable={true}
          enableComment={false}
          extraContext={{}}
          mentionsConfig={undefined}
          disableSlashCommands={true}
          disableBubbleMenuArtifactActions={true}
          placeholder="Add private notes..."
          showFixedMenu={false}
          className="flex-1 border-0 px-0 min-h-[calc(100vh-250px)]"
        />
      </div>
    </div>
  );
};

export default ProfilePrivateNotes;
